import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ServiceAdvisorService } from '../../services/serviceAdvisorService';
import { RealtimeService } from '../../services/realtimeService';
import { MechanicLogsService } from '../../services/mechanicLogsService';
import { AlertCircle, Search, Wrench, User, MapPin, Phone, Clock, CheckCircle, Calendar, Filter, X, Copy, FileText, Users, Plus } from 'lucide-react';

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'reached', label: 'Reached' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

const ServiceAdvisorComplaints = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [complaints, setComplaints] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Date filter states
  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    return yesterday;
  };

  const getToday = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  };

  const [showDateFilter, setShowDateFilter] = useState(false);
  const [startDate, setStartDate] = useState(getYesterday());
  const [endDate, setEndDate] = useState(getToday());
  const [dateFilterActive, setDateFilterActive] = useState(true);

  // Modals
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAssignAdvisorModal, setShowAssignAdvisorModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [selectedMechanic, setSelectedMechanic] = useState('');
  const [selectedAdvisorId, setSelectedAdvisorId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [isSelfAssigning, setIsSelfAssigning] = useState(false);
  const [isAssigningToAdvisor, setIsAssigningToAdvisor] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);

  // Fetch all complaints using getComplaintsFull (like mobile app)
  const loadComplaints = async (status = selectedTab) => {
    try {
      setLoading(true);
      if (!user?.id) {
        setComplaints([]);
        setStatusCounts({});
        return;
      }

      // Fetch all complaints
      const result = await ServiceAdvisorService.getComplaintsFull(user.id, '', true);
      if (!result.success || !Array.isArray(result.data)) {
        setComplaints([]);
        setStatusCounts({});
        return;
      }

      let allData = result.data || [];

      // Apply date filter if active
      if (dateFilterActive && (startDate || endDate)) {
        allData = allData.filter((complaint) => {
          const complaintDateStr = complaint.complaint_registered_at || complaint.requested_at;
          if (!complaintDateStr) return false;
          const complaintDate = new Date(complaintDateStr);
          if (isNaN(complaintDate.getTime())) return false;

          if (startDate && endDate) {
            return complaintDate >= startDate && complaintDate <= endDate;
          } else if (startDate) {
            return complaintDate >= startDate;
          } else if (endDate) {
            return complaintDate <= endDate;
          }
          return true;
        });
      }

      // Calculate status counts (only for complaints assigned to current advisor)
      const counts = {};
      allData.forEach((complaint) => {
        if (complaint.assigned_advisor_id === user.id) {
          const statusKey = complaint.request_status || 'pending';
          counts[statusKey] = (counts[statusKey] || 0) + 1;
        }
      });
      setStatusCounts(counts);

      // Filter by status tab
      let filteredData = allData;
      if (status === '') {
        // All tab - show only complaints assigned to current advisor
        filteredData = allData.filter((complaint) => complaint.assigned_advisor_id === user.id);
      } else {
        // Status tab - show complaints with that status assigned to current advisor
        filteredData = allData.filter(
          (complaint) =>
            complaint.request_status === status && complaint.assigned_advisor_id === user.id
        );
      }

      // Apply search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filteredData = filteredData.filter(
          (complaint) =>
            (complaint.customer_name || '').toLowerCase().includes(query) ||
            (complaint.contact_number || '').includes(query) ||
            (complaint.vehicle_number || '').toLowerCase().includes(query) ||
            (complaint.problem || '').toLowerCase().includes(query)
        );
      }

      setComplaints(filteredData);
    } catch (error) {
      console.error('Error loading complaints:', error);
      setComplaints([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Load advisors for assignment
  const { data: advisorsData } = useQuery({
    queryKey: ['serviceAdvisorAllAdvisors'],
    queryFn: () => ServiceAdvisorService.getAllAdvisors(),
    enabled: showAssignAdvisorModal,
  });

  const advisors = advisorsData?.data || [];

  // Load mechanics for assignment - EXACTLY like mobile app
  const { data: mechanicsData } = useQuery({
    queryKey: ['serviceAdvisorMechanicsForAssign', selectedComplaint?.pincode, selectedComplaint?.service_type],
    queryFn: () => ServiceAdvisorService.getAvailableMechanics(
      selectedComplaint?.service_type || null,
      null, // location
      selectedComplaint?.pincode || null
    ),
    enabled: showAssignModal && !!selectedComplaint,
  });

  const mechanics = mechanicsData?.data || [];

  // Load complaints on mount and when filters change
  useEffect(() => {
    loadComplaints();
  }, [selectedTab, dateFilterActive, startDate, endDate, searchQuery, user?.id]);

  // Set up real-time subscription
  useEffect(() => {
    if (!user?.id) return;

    const channel = RealtimeService.subscribeToServiceRequests((payload) => {
      console.log('Service request updated:', payload);
      loadComplaints();
    });

    return () => {
      RealtimeService.unsubscribe('service_requests');
    };
  }, [user?.id]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadComplaints();
  };

  const handleSelfAssign = async () => {
    if (!selectedComplaint) return;
    setIsSelfAssigning(true);
    try {
      const result = await ServiceAdvisorService.claimComplaint(
        selectedComplaint.id,
        user?.id,
        user?.name
      );
      if (result.success) {
        alert('Complaint claimed successfully!');
        setSelectedComplaint(null);
        loadComplaints();
      } else {
        alert('Failed to claim complaint: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error claiming complaint: ' + error.message);
    } finally {
      setIsSelfAssigning(false);
    }
  };

  const handleAssignToAdvisor = async () => {
    if (!selectedComplaint || !selectedAdvisorId) {
      alert('Please select an advisor');
      return;
    }

    const selectedAdvisor = advisors.find((a) => a.id === selectedAdvisorId);
    if (!selectedAdvisor) {
      alert('Invalid advisor selected');
      return;
    }

    setIsAssigningToAdvisor(true);
    try {
      const result = await ServiceAdvisorService.assignComplaint(
        selectedComplaint.id,
        user?.id,
        selectedAdvisorId,
        user?.name,
        selectedAdvisor.name || selectedAdvisor.full_name || 'Unknown'
      );
      if (result.success) {
        alert('Complaint assigned successfully!');
        setShowAssignAdvisorModal(false);
        setSelectedComplaint(null);
        setSelectedAdvisorId('');
        loadComplaints();
      } else {
        alert('Failed to assign complaint: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error assigning complaint: ' + error.message);
    } finally {
      setIsAssigningToAdvisor(false);
    }
  };

  const handleUnassign = async () => {
    if (!selectedComplaint) return;
    setIsUnassigning(true);
    try {
      const result = await ServiceAdvisorService.unassignComplaint(selectedComplaint.id, user?.id);
      if (result.success) {
        alert('Complaint unassigned successfully!');
        setShowUnassignModal(false);
        setSelectedComplaint(null);
        loadComplaints();
      } else {
        alert('Failed to unassign complaint: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error unassigning complaint: ' + error.message);
    } finally {
      setIsUnassigning(false);
    }
  };

  const handleAssignMechanic = async () => {
    if (!selectedComplaint || !selectedMechanic) {
      alert('Please select a mechanic');
      return;
    }

    const mechanic = mechanics.find((m) => m.id === selectedMechanic);
    if (!mechanic) {
      alert('Selected mechanic not found');
      return;
    }

    setIsAssigning(true);
    try {
      // Use assignMechanicToRequest with all parameters exactly like mobile app
      const result = await ServiceAdvisorService.assignMechanicToRequest(
        selectedComplaint.id,
        selectedMechanic,
        mechanic.name || 'Unknown',
        mechanic.contact_number || mechanic.phone || '',
        null, // dispatchRemark
        null, // mechanicPayment
        null  // assignmentRemark
      );

      if (result.success) {
        alert(result.message || 'Mechanic assigned successfully!');
        setShowAssignModal(false);
        setSelectedComplaint(null);
        setSelectedMechanic('');
        loadComplaints();
      } else {
        alert('Failed to assign mechanic: ' + (result.error || result.message || 'Unknown error'));
      }
    } catch (error) {
      alert('Error assigning mechanic: ' + error.message);
    } finally {
      setIsAssigning(false);
    }
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'in_progress' || s === 'reached') return 'bg-blue-100 text-blue-700';
    if (s === 'assigned') return 'bg-yellow-100 text-yellow-700';
    if (s === 'pending') return 'bg-orange-100 text-orange-700';
    if (s === 'cancelled') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Complaints</h1>
          <p className="text-gray-600 mt-1">Manage service complaints</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowDateFilter(!showDateFilter)}
            className={`px-4 py-2 rounded-lg border flex items-center space-x-2 ${
              dateFilterActive
                ? 'bg-teal-500 text-white border-teal-500'
                : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            <Calendar size={18} />
            <span>Date Filter</span>
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 rounded-lg border bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Date Filter */}
      {showDateFilter && (
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Date Range Filter</h3>
            <button
              onClick={() => setShowDateFilter(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={startDate ? startDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={endDate ? endDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setDateFilterActive(!dateFilterActive);
                  loadComplaints();
                }}
                className={`w-full px-4 py-2 rounded-lg ${
                  dateFilterActive
                    ? 'bg-teal-500 text-white'
                    : 'bg-gray-200 text-gray-700'
            }`}
          >
                {dateFilterActive ? 'Filter Active' : 'Enable Filter'}
          </button>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by customer name, phone, vehicle number, or problem..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
          />
        </div>
      </div>

      {/* Status Tabs */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex items-center space-x-2 overflow-x-auto pb-2">
          {STATUS_TABS.map((tab) => (
          <button
              key={tab.key}
              onClick={() => setSelectedTab(tab.key)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap flex items-center space-x-2 ${
                selectedTab === tab.key
                  ? 'bg-teal-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
              <span>{tab.label}</span>
              {statusCounts[tab.key] !== undefined && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  selectedTab === tab.key ? 'bg-white/20' : 'bg-gray-200'
                }`}>
                  {statusCounts[tab.key] || 0}
                </span>
              )}
          </button>
          ))}
        </div>
      </div>

      {/* Complaints List */}
      {loading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading complaints...</p>
        </div>
      ) : complaints.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No complaints found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {complaints.map((complaint) => (
            <div key={complaint.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {complaint.customer_name || 'Unknown Customer'}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(complaint.request_status)}`}>
                      {complaint.request_status || 'Pending'}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-3">{complaint.problem || complaint.description || 'No description'}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {complaint.contact_number && (
                      <div>
                        <p className="text-gray-500">Contact</p>
                        <a href={`tel:${complaint.contact_number}`} className="font-medium text-gray-900 hover:text-teal-600">
                          {complaint.contact_number}
                        </a>
                      </div>
                    )}
                    {complaint.vehicle_number && (
                      <div>
                        <p className="text-gray-500">Vehicle</p>
                        <p className="font-medium text-gray-900">{complaint.vehicle_number}</p>
                      </div>
                    )}
                    {complaint.address && (
                      <div>
                        <p className="text-gray-500">Address</p>
                        <p className="font-medium text-gray-900">{complaint.address}</p>
                      </div>
                    )}
                    {complaint.requested_at && (
                      <div>
                        <p className="text-gray-500">Requested</p>
                        <p className="font-medium text-gray-900">{formatDate(complaint.requested_at)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-2 pt-4 border-t border-gray-200 flex-wrap gap-2">
                <button
                  onClick={() => navigate(`/service_advisor/complaints/${complaint.id}`)}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
                >
                  View Details
                </button>
                {!complaint.assigned_advisor_id && (
                  <button
                    onClick={async () => {
                      setIsSelfAssigning(complaint.id);
                      try {
                        const result = await ServiceAdvisorService.claimComplaint(
                          complaint.id,
                          user?.id,
                          user?.name
                        );
                        if (result.success) {
                          alert('Complaint claimed successfully!');
                          loadComplaints();
                        } else {
                          alert('Failed to claim complaint: ' + (result.error || 'Unknown error'));
                        }
                      } catch (error) {
                        alert('Error claiming complaint: ' + error.message);
                      } finally {
                        setIsSelfAssigning(null);
                      }
                    }}
                    disabled={isSelfAssigning === complaint.id}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                  >
                    {isSelfAssigning === complaint.id ? 'Claiming...' : 'Self Allocate'}
                  </button>
                )}
                {!complaint.assigned_mechanic_id && (
                  <button
                    onClick={() => navigate(`/service_advisor/assign-mechanic/${complaint.id}`, {
                      state: { complaint: complaint }
                    })}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Assign Mechanic
                  </button>
                )}
                {complaint.assigned_advisor_id === user.id && (
                  <>
                  <button 
                    onClick={() => {
                      setSelectedComplaint(complaint);
                        setShowAssignAdvisorModal(true);
                    }}
                      className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
                  >
                      Transfer Advisor
                  </button>
                    <button
                      onClick={() => {
                        setSelectedComplaint(complaint);
                        const reason = prompt('Enter cancellation reason:');
                        if (reason) {
                          handleCancelService(complaint, reason);
                        }
                      }}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                    >
                      Cancel Service
                    </button>
                    <button
                      onClick={() => {
                        setSelectedComplaint(complaint);
                        setShowUnassignModal(true);
                      }}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      Unassign
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assign Mechanic Modal */}
      {showAssignModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Assign Mechanic</h2>
            <p className="text-gray-600 mb-4">
              Select a mechanic to assign to complaint #{selectedComplaint?.id?.slice(0, 8)}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Mechanic</label>
              <select
                value={selectedMechanic}
                onChange={(e) => setSelectedMechanic(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select mechanic...</option>
                {mechanics.map((mechanic) => (
                  <option key={mechanic.id} value={mechanic.id}>
                    {mechanic.name || mechanic.mechanic_name || 'Unknown'} - {mechanic.contact_number || mechanic.number || 'N/A'}
                    {(mechanic.timing || mechanic.timings) ? ` (Timing: ${mechanic.timing || mechanic.timings})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedComplaint(null);
                  setSelectedMechanic('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignMechanic}
                disabled={!selectedMechanic || isAssigning}
                className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
              >
                {isAssigning ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign to Advisor Modal */}
      {showAssignAdvisorModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Assign to Advisor</h2>
            <p className="text-gray-600 mb-4">
              Select an advisor to assign complaint #{selectedComplaint?.id?.slice(0, 8)}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Advisor</label>
              <select
                value={selectedAdvisorId}
                onChange={(e) => setSelectedAdvisorId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select advisor...</option>
                {advisors
                  .filter((a) => a.id !== user.id)
                  .map((advisor) => (
                    <option key={advisor.id} value={advisor.id}>
                      {advisor.name || advisor.full_name || 'Unknown'}
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowAssignAdvisorModal(false);
                  setSelectedComplaint(null);
                  setSelectedAdvisorId('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignToAdvisor}
                disabled={!selectedAdvisorId || isAssigningToAdvisor}
                className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50"
              >
                {isAssigningToAdvisor ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unassign Modal */}
      {showUnassignModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Unassign Complaint</h2>
            <p className="text-gray-600 mb-4">
              Are you sure you want to unassign complaint #{selectedComplaint?.id?.slice(0, 8)}? This will make it available for reassignment.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowUnassignModal(false);
                  setSelectedComplaint(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUnassign}
                disabled={isUnassigning}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {isUnassigning ? 'Unassigning...' : 'Unassign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ServiceAdvisorRegistered = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [selectedMechanic, setSelectedMechanic] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load unassigned complaints
  const loadComplaints = async () => {
    try {
      setLoading(true);
      if (!user?.id) {
        setComplaints([]);
        return;
      }

      // Fetch all complaints and filter unassigned ones
      const result = await ServiceAdvisorService.getComplaintsFull(user.id, '', true);
      if (result.success && Array.isArray(result.data)) {
        const unassigned = result.data.filter((c) => !c.assigned_advisor_id);
        setComplaints(unassigned);
      } else {
        setComplaints([]);
      }
    } catch (error) {
      console.error('Error loading registered complaints:', error);
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComplaints();
  }, [user?.id]);

  // Set up real-time subscription
  useEffect(() => {
    if (!user?.id) return;

    const channel = RealtimeService.subscribeToServiceRequests((payload) => {
      console.log('Service request updated:', payload);
      loadComplaints();
    });

    return () => {
      RealtimeService.unsubscribe('service_requests');
    };
  }, [user?.id]);

  // Load mechanics for assignment - EXACTLY like mobile app
  const { data: mechanicsData } = useQuery({
    queryKey: ['serviceAdvisorMechanicsForAssign', selectedComplaint?.pincode, selectedComplaint?.service_type],
    queryFn: () => ServiceAdvisorService.getAvailableMechanics(
      selectedComplaint?.service_type || null,
      null, // location
      selectedComplaint?.pincode || null
    ),
    enabled: showAssignModal && !!selectedComplaint,
  });

  const mechanics = mechanicsData?.data || [];

  const handleClaimComplaint = async () => {
    if (!selectedComplaint) return;

    setIsClaiming(true);
    try {
      const result = await ServiceAdvisorService.claimComplaint(
        selectedComplaint.id,
        user?.id,
        user?.name
      );
      if (result.success) {
        alert('Complaint claimed successfully!');
        setShowClaimModal(false);
        setSelectedComplaint(null);
        loadComplaints();
      } else {
        alert('Failed to claim complaint: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error claiming complaint: ' + error.message);
    } finally {
      setIsClaiming(false);
    }
  };

  const handleAssignMechanic = async () => {
    if (!selectedComplaint || !selectedMechanic) {
      alert('Please select a mechanic');
      return;
    }

    const mechanic = mechanics.find((m) => m.id === selectedMechanic);
    if (!mechanic) {
      alert('Selected mechanic not found');
      return;
    }

    setIsAssigning(true);
    try {
      // Use assignMechanicToRequest with all parameters exactly like mobile app
      const result = await ServiceAdvisorService.assignMechanicToRequest(
        selectedComplaint.id,
        selectedMechanic,
        mechanic.name || 'Unknown',
        mechanic.contact_number || mechanic.phone || '',
        null, // dispatchRemark
        null, // mechanicPayment
        null  // assignmentRemark
      );

      if (result.success) {
        alert(result.message || 'Mechanic assigned successfully!');
        setShowAssignModal(false);
        setSelectedComplaint(null);
        setSelectedMechanic('');
        loadComplaints();
      } else {
        alert('Failed to assign mechanic: ' + (result.error || result.message || 'Unknown error'));
      }
    } catch (error) {
      alert('Error assigning mechanic: ' + error.message);
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Registered Complaints</h1>
        <p className="text-gray-600 mt-1">View unassigned complaints</p>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading complaints...</p>
        </div>
      ) : complaints.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No registered complaints</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Problem</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registered</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {complaints.map((complaint) => (
                <tr key={complaint.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">#{complaint.id?.slice(0, 8)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{complaint.customer_name || 'N/A'}</td>
                  <td className="px-6 py-4 text-gray-600">{complaint.problem || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {complaint.complaint_registered_at || complaint.requested_at
                      ? new Date(complaint.complaint_registered_at || complaint.requested_at).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      {!complaint.assigned_advisor_id && (
                        <button 
                          onClick={() => {
                            setSelectedComplaint(complaint);
                            setShowClaimModal(true);
                          }}
                          className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                        >
                          Claim
                        </button>
                      )}
                      <button 
                        onClick={() => {
                          setSelectedComplaint(complaint);
                          setShowAssignModal(true);
                        }}
                        className="px-3 py-1 bg-teal-500 text-white rounded-lg hover:bg-teal-600 text-sm"
                      >
                        Assign Mechanic
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Claim Modal */}
      {showClaimModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Claim Complaint</h2>
            <p className="text-gray-600 mb-4">
              Are you sure you want to claim complaint #{selectedComplaint?.id?.slice(0, 8)}?
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowClaimModal(false);
                  setSelectedComplaint(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleClaimComplaint}
                disabled={isClaiming}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                {isClaiming ? 'Claiming...' : 'Claim'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Mechanic Modal */}
      {showAssignModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Assign Mechanic</h2>
            <p className="text-gray-600 mb-4">
              Select a mechanic to assign to complaint #{selectedComplaint?.id?.slice(0, 8)}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Mechanic</label>
              <select
                value={selectedMechanic}
                onChange={(e) => setSelectedMechanic(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select mechanic...</option>
                {mechanics.map((mechanic) => (
                  <option key={mechanic.id} value={mechanic.id}>
                    {mechanic.name || mechanic.mechanic_name || 'Unknown'} - {mechanic.contact_number || mechanic.number || 'N/A'}
                    {(mechanic.timing || mechanic.timings) ? ` (Timing: ${mechanic.timing || mechanic.timings})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedComplaint(null);
                  setSelectedMechanic('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignMechanic}
                disabled={!selectedMechanic || isAssigning}
                className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
              >
                {isAssigning ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ServiceAdvisorMechanics = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchPincode, setSearchPincode] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [debouncedSearchPincode, setDebouncedSearchPincode] = useState('');
  const [showAddMechanicModal, setShowAddMechanicModal] = useState(false);
  const [isAddingMechanic, setIsAddingMechanic] = useState(false);
  const [newMechanic, setNewMechanic] = useState({
    mechanic_name: '',
    number: '',
    alternate_number1: '',
    alternate_number2: '',
    service_tag: '',
    service_tag2: '',
    service_tag3: '',
    timing: '',
    service_areas: [{ area: '', pincode: '', state: '' }],
  });

  // Debounce search queries to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchPincode(searchPincode);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchPincode]);

  const { data: mechanicsData, isLoading, refetch } = useQuery({
    queryKey: ['serviceAdvisorMechanics', debouncedSearchQuery, debouncedSearchPincode],
    queryFn: () => {
      if (debouncedSearchQuery.trim().length > 0) {
        return ServiceAdvisorService.searchMechanics(debouncedSearchQuery, null);
      }
      return ServiceAdvisorService.getAvailableMechanics(
        null, // serviceType
        null, // location
        debouncedSearchPincode || null // pincode
      );
    },
    enabled: true,
    staleTime: 30000, // Cache for 30 seconds
  });
  const mechanics = mechanicsData?.data || [];

  // Filter mechanics by search query client-side only for non-search fetches
  const filteredMechanics = debouncedSearchQuery
    ? mechanics
    : mechanics;

  const generateMechanicCode = () => {
    const tail = String(Date.now()).slice(-6);
    return `M${tail}`;
  };

  const handleSaveNewMechanic = async () => {
    const name = (newMechanic.mechanic_name || '').trim();
    const numDigits = (newMechanic.number || '').replace(/\D/g, '');
    if (!name) return alert('Mechanic name is required');
    if (numDigits.length < 10) return alert('Mechanic number must be at least 10 digits');

    setIsAddingMechanic(true);
    try {
      const res = await ServiceAdvisorService.addMechanicSimple({
        code: generateMechanicCode(),
        mechanic_name: name,
        number: numDigits,
        alternate_number1: (newMechanic.alternate_number1 || '').replace(/\D/g, '') || null,
        alternate_number2: (newMechanic.alternate_number2 || '').replace(/\D/g, '') || null,
        service_tag: (newMechanic.service_tag || '').trim() || null,
        service_tag2: (newMechanic.service_tag2 || '').trim() || null,
        service_tag3: (newMechanic.service_tag3 || '').trim() || null,
        timing: (newMechanic.timing || '').trim() || null,
        service_areas: Array.isArray(newMechanic.service_areas) ? newMechanic.service_areas : [],
      });
      if (!res.success) throw new Error(res.error || 'Failed to add mechanic');

      setShowAddMechanicModal(false);
      setNewMechanic({
        mechanic_name: '',
        number: '',
        alternate_number1: '',
        alternate_number2: '',
        service_tag: '',
        service_tag2: '',
        service_tag3: '',
        timing: '',
        service_areas: [{ area: '', pincode: searchPincode || '', state: '' }],
      });
      await refetch();
      alert('Mechanic added successfully!');
    } catch (e) {
      alert(e?.message || String(e));
    } finally {
      setIsAddingMechanic(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mechanics</h1>
          <p className="text-gray-600 mt-1">Search and manage mechanics</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setNewMechanic((p) => {
              const arr = Array.isArray(p.service_areas) && p.service_areas.length ? [...p.service_areas] : [{ area: '', pincode: '', state: '' }];
              if (!arr[0]) arr[0] = { area: '', pincode: '', state: '' };
              arr[0] = { ...arr[0], pincode: arr[0].pincode || searchPincode || '' };
              return { ...p, service_areas: arr };
            });
            setShowAddMechanicModal(true);
          }}
          className="px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 text-sm font-medium flex items-center justify-center gap-2"
          title="Add mechanic"
        >
          <Plus size={16} />
          Add Mechanic
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by pincode..."
              value={searchPincode}
              onChange={(e) => setSearchPincode(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Loading mechanics...</p>
          </div>
        ) : filteredMechanics.length === 0 ? (
          <div className="text-center py-12">
            <Wrench size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No mechanics found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMechanics.map((mechanic) => (
              <div key={mechanic.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <User size={24} className="text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {mechanic.name || mechanic.mechanic_name || 'Unknown'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {mechanic.contact_number || mechanic.number || 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  {mechanic.pincode && (
                    <p>
                      <span className="font-medium">Pincode:</span> {mechanic.pincode}
                    </p>
                  )}
                  {(mechanic.timing || mechanic.timings) && (
                    <p>
                      <span className="font-medium">Timing:</span> {mechanic.timing || mechanic.timings}
                    </p>
                  )}
                  {mechanic.service_areas && (
                    <p>
                      <span className="font-medium">Areas:</span>{' '}
                      {Array.isArray(mechanic.service_areas) ? (
                        mechanic.service_areas.map((area, idx) => 
                          typeof area === 'object' && area !== null
                            ? area.area || area.pincode || JSON.stringify(area)
                            : area
                        ).join(', ')
                      ) : typeof mechanic.service_areas === 'object' && mechanic.service_areas !== null ? (
                        mechanic.service_areas.area || mechanic.service_areas.pincode || JSON.stringify(mechanic.service_areas)
                      ) : (
                        String(mechanic.service_areas)
                      )}
                    </p>
                  )}
                  {mechanic.mechanic_name && (
                    <p>
                      <span className="font-medium">Name:</span> {mechanic.mechanic_name}
                    </p>
                  )}
                  {mechanic.number && (
                    <p>
                      <span className="font-medium">Contact:</span> {mechanic.number}
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-2 mt-4">
                  <button
                    onClick={() => {
                      const info = [
                        `Name: ${mechanic.name || mechanic.mechanic_name || 'N/A'}`,
                        `Phone: ${mechanic.contact_number || mechanic.number || 'N/A'}`,
                        `Pincode: ${mechanic.pincode || 'N/A'}`,
                        `Service Tag: ${mechanic.service_tag || 'N/A'}`,
                        `Timing: ${mechanic.timing || mechanic.timings || 'N/A'}`
                      ].join('\n');
                      navigator.clipboard.writeText(info);
                      alert('Mechanic info copied to clipboard!');
                    }}
                    className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center space-x-1 text-sm"
                    title="Copy Info"
                  >
                    <Copy size={14} />
                    <span>Copy</span>
                  </button>
                  {(mechanic.contact_number || mechanic.number) && (
                    <button
                      onClick={() => window.location.href = `tel:${mechanic.contact_number || mechanic.number}`}
                      className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center space-x-1 text-sm"
                      title="Call"
                    >
                      <Phone size={14} />
                      <span>Call</span>
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/service_advisor/mechanics/${mechanic.id}/logs`, {
                      state: { mechanic: mechanic }
                    })}
                    className="flex-1 px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center justify-center space-x-1 text-sm"
                    title="View Logs"
                  >
                    <FileText size={14} />
                    <span>Logs</span>
                </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Mechanic Modal */}
      {showAddMechanicModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 bg-teal-600 text-white flex items-center justify-between">
              <div className="font-semibold">Add Mechanic</div>
              <button
                type="button"
                className="p-2 rounded-lg hover:bg-white/15"
                onClick={() => setShowAddMechanicModal(false)}
                disabled={isAddingMechanic}
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mechanic Name *</label>
                <input
                  value={newMechanic.mechanic_name}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, mechanic_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g. Ravi Kumar"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  value={newMechanic.number}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="10 digit number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timing</label>
                <input
                  value={newMechanic.timing}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, timing: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g. 9am-9pm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alt Number 1</label>
                <input
                  value={newMechanic.alternate_number1}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, alternate_number1: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alt Number 2</label>
                <input
                  value={newMechanic.alternate_number2}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, alternate_number2: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Tag</label>
                <input
                  value={newMechanic.service_tag}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, service_tag: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Tag 2</label>
                <input
                  value={newMechanic.service_tag2}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, service_tag2: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <label className="block text-sm font-medium text-gray-700">Service Areas (max 20)</label>
                  <button
                    type="button"
                    onClick={() =>
                      setNewMechanic((p) => {
                        const arr = Array.isArray(p.service_areas) ? [...p.service_areas] : [];
                        if (arr.length >= 20) return p;
                        arr.push({ area: '', pincode: searchPincode || '', state: '' });
                        return { ...p, service_areas: arr };
                      })
                    }
                    className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium flex items-center gap-2"
                    disabled={(newMechanic.service_areas?.length || 0) >= 20}
                    title="Add another service area"
                  >
                    <Plus size={14} />
                    Add Area
                  </button>
                </div>

                <div className="space-y-2">
                  {(Array.isArray(newMechanic.service_areas) ? newMechanic.service_areas : []).map((row, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2">
                      <input
                        value={row?.area || ''}
                        onChange={(e) =>
                          setNewMechanic((p) => {
                            const arr = Array.isArray(p.service_areas) ? [...p.service_areas] : [];
                            arr[idx] = { ...(arr[idx] || {}), area: e.target.value };
                            return { ...p, service_areas: arr };
                          })
                        }
                        className="md:col-span-5 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        placeholder="Area"
                      />
                      <input
                        value={row?.pincode || ''}
                        onChange={(e) =>
                          setNewMechanic((p) => {
                            const arr = Array.isArray(p.service_areas) ? [...p.service_areas] : [];
                            arr[idx] = { ...(arr[idx] || {}), pincode: e.target.value };
                            return { ...p, service_areas: arr };
                          })
                        }
                        className="md:col-span-3 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        placeholder="Pincode"
                      />
                      <input
                        value={row?.state || ''}
                        onChange={(e) =>
                          setNewMechanic((p) => {
                            const arr = Array.isArray(p.service_areas) ? [...p.service_areas] : [];
                            arr[idx] = { ...(arr[idx] || {}), state: e.target.value };
                            return { ...p, service_areas: arr };
                          })
                        }
                        className="md:col-span-3 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        placeholder="State"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setNewMechanic((p) => {
                            const arr = Array.isArray(p.service_areas) ? [...p.service_areas] : [];
                            if (arr.length <= 1) {
                              arr[0] = { area: '', pincode: searchPincode || '', state: '' };
                              return { ...p, service_areas: arr };
                            }
                            arr.splice(idx, 1);
                            return { ...p, service_areas: arr };
                          })
                        }
                        className="md:col-span-1 w-full px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                        title="Remove"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Note: kisi area ko valid banane ke liye <b>Area + Pincode + State</b> teeno fill karo.
                </p>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setShowAddMechanicModal(false)}
                disabled={isAddingMechanic}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                onClick={handleSaveNewMechanic}
                disabled={isAddingMechanic}
              >
                {isAddingMechanic ? 'Saving…' : 'Save Mechanic'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { ServiceAdvisorComplaints, ServiceAdvisorRegistered, ServiceAdvisorMechanics };
